import React, { useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useRegisterSW } from 'virtual:pwa-register/react';

export const ServiceWorkerUpdateModal: React.FC = () => {
  const languageContext = useContext(LanguageProviderContext);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      console.log('Service Worker registered:', r);
    },
    onRegisterError(error) {
      console.error('Service Worker registration error:', error);
    },
    onOfflineReady() {
      console.log('App is ready to work offline');
    },
    onNeedRefresh() {
      console.log('New content available, showing update prompt');
    },
  });

  const handleUpdateNow = () => {
    updateServiceWorker(true);
    setNeedRefresh(false);
  };

  const handleLater = () => {
    setNeedRefresh(false);
    // The update will be available on next check
  };

  // Show offline ready notification (optional - can be removed if not needed)
  const handleOfflineReadyClose = () => {
    setOfflineReady(false);
  };

  if (!languageContext) return null;

  const { translator } = languageContext;

  const isOpen = needRefresh;

  return (
    <>
      {/* Update Available Modal - Non-dismissible */}
      <Dialog 
        open={isOpen} 
        onOpenChange={(open) => {
          // DialogContent already prevents escape key and outside clicks.
          // Only allow closing through explicit button handlers (handleUpdateNow/handleLater)
          // If somehow a close is attempted, ignore it to ensure user must take action
          if (!open) {
            // Do nothing - user must click a button
            return;
          }
        }}
      >
        <DialogContent hideCloseButton className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              {translator('ServiceWorker.updateAvailable') || 'Update Available'}
            </DialogTitle>
            <DialogDescription>
              {translator('ServiceWorker.updateDescription') || 
                'A new version of the app is available. Would you like to update now?'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleLater} className="w-full sm:w-auto">
              {translator('ServiceWorker.later') || 'Later'}
            </Button>
            <Button onClick={handleUpdateNow} className="w-full sm:w-auto">
              {translator('ServiceWorker.updateNow') || 'Update Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offline Ready Notification (optional - dismissible) */}
      {offlineReady && (
        <Dialog open={offlineReady} onOpenChange={handleOfflineReadyClose}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>
                {translator('ServiceWorker.offlineReady') || 'App Ready Offline'}
              </DialogTitle>
              <DialogDescription>
                {translator('ServiceWorker.offlineReadyDescription') || 
                  'The app is now ready to work offline.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={handleOfflineReadyClose}>
                {translator('Global.close') || 'Close'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
